import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { fmt } from '../utils/format';

// Shared between BlockedOrders.jsx, FollowUpSchedule.jsx and
// BackOrderManagement.jsx so all three pages get identical, fully-interactive
// order blocks (notes, next action, follow-up, assignment, urgent flag)
// rather than drifting copies. Notes are keyed by order_id, not stage, so an
// order's full history follows it automatically if it later moves between
// these stages (e.g. a back order escalating to Problem Encountered).

export const stageMap = {
  'Problem Encountered': { color: 'gold', label: 'Problem Encountered' },
  'To Call Customer':    { color: 'gold', label: 'To Call Customer' },
  'HC - Back Order':     { color: 'blue', label: 'Back Order' },
};

export const daysSince = (dateStr) => {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
};

export const formatNoteDate = (dateStr) =>
  new Date(dateStr).toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' });

export const formatDateNZ = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });

export const todayISO = () => new Date().toLocaleDateString('en-CA');
export const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-CA');
};

// Cin7's own label for the back order stage -- exact string matters, it is
// PUT straight through to the API by set_stage_from_queue.py.
export const BACKORDER_STAGE = 'HC - Back Order';

// ── Notes log + composer ──────────────────────────────────────────────────────
// allowSetBackorder + orderRef + source opt a page into the "Set to Backorder"
// choice. Pages that don't pass them (SOBlock, and so BlockedOrders /
// BackOrderManagement / FollowUpSchedule) never render it -- on Back Order
// Management in particular the order is already at that stage.
//
// It is a dropdown on the composer rather than its own button, and the stage
// change fires on Save, deliberately: staff would otherwise hit a standalone
// button first and move the order with no note, no initials, no next action
// and no follow-up date against it. Tying it to Save means a stage change can
// never be recorded without all of that captured alongside it.
export const NotesPanel = ({ orderId, orderRef, stage, notes, staffList, nextActions, onAdded, allowSetBackorder, source }) => {
  const [text, setText]           = useState('');
  const [initials, setInitials]   = useState('');
  const [nextActionId, setNextActionId] = useState('');
  const [followUpOption, setFollowUpOption] = useState(''); // 'today' | 'tomorrow' | 'custom'
  const [customDate, setCustomDate] = useState('');
  const [saving, setSaving]       = useState(false);
  const [setToBackorder, setSetToBackorder] = useState('no'); // 'no' | 'yes' -- defaults to No
  const [backorderMsg, setBackorderMsg]     = useState(null); // { ok, text } shown under the composer

  const resolvedFollowUpDate =
    followUpOption === 'today'    ? todayISO() :
    followUpOption === 'tomorrow' ? tomorrowISO() :
    followUpOption === 'custom'   ? customDate :
    '';

  // Initials are required, not optional: every note is a record of who worked
  // the order, and a Set to Backorder saved without them writes an unattributed
  // stage change into Cin7's internal comments.
  const canSave = !!text.trim() && !!initials && !!nextActionId && !!resolvedFollowUpDate && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setBackorderMsg(null);

    const wantsBackorder = allowSetBackorder && !!orderRef && setToBackorder === 'yes';
    const from           = source || 'Admin Problem Encountered Page';
    const nextAction     = nextActions.find(a => String(a.id) === String(nextActionId));
    const prefixedNote   =
      `[${nextAction?.label || 'Next action'} · Follow up ${formatDateNZ(resolvedFollowUpDate)}] ${text.trim()}` +
      (wantsBackorder ? ` · Set to ${BACKORDER_STAGE}` : '');

    const { data, error } = await supabase
      .from('blocked_order_notes')
      .insert({
        order_id:        orderId,
        note:            prefixedNote,
        initials:        initials || null,
        stage_at_note:   stage,
        next_action_id:  nextActionId,
        follow_up_date:  resolvedFollowUpDate,
      })
      .select()
      .single();

    // Note first, stage second. If the note can't be written the order is left
    // where it is -- moving it with no record of who moved it or why is exactly
    // what tying this to Save is meant to prevent.
    if (error || !data) {
      setSaving(false);
      alert(`Failed to save note: ${error?.message || 'unknown error'}`);
      return;
    }

    // Queued rather than called directly: the browser has no Cin7 credentials.
    // set_stage_from_queue.py picks the row up on the poller's ~1 minute cycle
    // and PUTs stage + internalComments together via
    // update_sales_order_stage_with_comment(), which appends to the existing
    // comments instead of overwriting them.
    if (wantsBackorder) {
      const stamp   = new Date().toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' });
      const who     = initials ? ` by ${initials}` : '';
      const comment = `${stamp} - Moved to ${BACKORDER_STAGE} from ${from}${who}.`;

      const { error: queueError } = await supabase
        .from('sales_order_stage_updates')
        .insert({
          order_ref:        orderRef,
          order_id:         orderId,
          stage:            BACKORDER_STAGE,
          internal_comment: comment,
        });

      // The note is already saved at this point, so don't imply nothing
      // happened -- say exactly which half failed. Stays on screen either way.
      setBackorderMsg(queueError
        ? { ok: false, text: `Note saved, but the stage change could not be queued: ${queueError.message}` }
        : { ok: true,  text: `Queued — Cin7 moves to ${BACKORDER_STAGE} on the next poller run (about a minute), then replicates back.` });
    }

    setSaving(false);
    onAdded(orderId, data);
    setText('');
    setNextActionId('');
    setFollowUpOption('');
    setCustomDate('');
    setSetToBackorder('no');
  };

  const followUpBtnStyle = { fontSize: 11, padding: '3px 10px' };

  return (
    <div style={{ margin: '0 12px 8px' }}>
      {notes.length > 0 && (
        <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {notes.map((n) => (
            <div key={n.id} style={{ fontSize: 12, color: 'var(--muted)' }}>
              <span style={{ color: 'var(--white)' }}>{formatNoteDate(n.created_at)}</span>
              {n.initials && <span style={{ color: 'var(--gold)', marginLeft: 6 }}>[{n.initials}]</span>}
              <span style={{ marginLeft: 6 }}>{n.note}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            value={initials}
            onChange={(e) => setInitials(e.target.value)}
            style={{
              background: 'var(--bg3)', color: 'var(--white)',
              border: '1px solid var(--border2)', borderRadius: 4,
              padding: '4px 8px', fontFamily: 'var(--font-body)', fontSize: 12,
            }}
          >
            <option value="">Who…</option>
            {staffList.map((s) => (
              <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
            ))}
          </select>
          <input
            className="note-input"
            type="text"
            placeholder="Add note…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            style={{ flex: 1 }}
          />
          <button
            className="action-btn action-btn--close"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? '…' : 'Save'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            value={nextActionId}
            onChange={(e) => setNextActionId(e.target.value)}
            style={{
              background: 'var(--bg3)', color: 'var(--white)',
              border: '1px solid var(--border2)', borderRadius: 4,
              padding: '4px 8px', fontFamily: 'var(--font-body)', fontSize: 12,
            }}
          >
            <option value="">Next Action…</option>
            {nextActions.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>

          {allowSetBackorder && orderRef && (
            <select
              value={setToBackorder}
              onChange={(e) => setSetToBackorder(e.target.value)}
              title={`Set to Backorder — on Save, queue a Cin7 stage change to ${BACKORDER_STAGE} with an internal comment`}
              style={{
                background: 'var(--bg3)',
                color: setToBackorder === 'yes' ? 'var(--gold)' : 'var(--white)',
                border: `1px solid ${setToBackorder === 'yes' ? 'var(--gold)' : 'var(--border2)'}`,
                borderRadius: 4, padding: '4px 8px',
                fontFamily: 'var(--font-body)', fontSize: 12,
                fontWeight: setToBackorder === 'yes' ? 700 : 400,
              }}
            >
              <option value="no">Set to Backorder: No</option>
              <option value="yes">Set to Backorder: Yes</option>
            </select>
          )}
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>Follow up:</span>
          <button
            className={`action-btn ${followUpOption === 'today' ? 'action-btn--active' : 'action-btn--ghost'}`}
            style={followUpBtnStyle}
            onClick={() => setFollowUpOption('today')}
          >
            By End of Today
          </button>
          <button
            className={`action-btn ${followUpOption === 'tomorrow' ? 'action-btn--active' : 'action-btn--ghost'}`}
            style={followUpBtnStyle}
            onClick={() => setFollowUpOption('tomorrow')}
          >
            Tomorrow
          </button>
          <button
            className={`action-btn ${followUpOption === 'custom' ? 'action-btn--active' : 'action-btn--ghost'}`}
            style={followUpBtnStyle}
            onClick={() => setFollowUpOption('custom')}
          >
            On a Date
          </button>
          {followUpOption === 'custom' && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              style={{
                background: 'var(--bg3)', color: 'var(--white)',
                border: '1px solid var(--border2)', borderRadius: 4,
                padding: '3px 8px', fontFamily: 'var(--font-body)', fontSize: 11,
              }}
            />
          )}
        </div>

        {backorderMsg && (
          <div style={{ fontSize: 11, color: backorderMsg.ok ? 'var(--muted)' : 'var(--red-light, #f87171)' }}>
            {backorderMsg.text}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Assigned To dropdown ────────────────────────────────────────────────────────
export const AssignDropdown = ({ orderId, assignedInitials, staffList, onAssign }) => {
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const initials = e.target.value;
    setSaving(true);
    const { error } = await supabase
      .from('blocked_order_assignments')
      .upsert({ order_id: orderId, initials: initials || null, updated_at: new Date().toISOString() });
    setSaving(false);
    if (!error) {
      onAssign(orderId, initials || null);
    } else {
      alert(`Failed to update assignment: ${error.message}`);
    }
  };

  return (
    <select
      value={assignedInitials || ''}
      onChange={handleChange}
      disabled={saving}
      style={{
        background: 'var(--bg3)', color: 'var(--white)',
        border: '1px solid var(--border2)', borderRadius: 4,
        padding: '3px 6px', fontFamily: 'var(--font-body)', fontSize: 11,
      }}
      title="Assigned To"
    >
      <option value="">Unassigned</option>
      {staffList.map((s) => (
        <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
      ))}
    </select>
  );
};

// ── Urgent checkbox ─────────────────────────────────────────────────────────────
export const UrgentCheckbox = ({ orderId, urgent, onUrgentChange }) => {
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const checked = e.target.checked;
    setSaving(true);
    const { error } = await supabase
      .from('blocked_order_assignments')
      .upsert({ order_id: orderId, urgent: checked, updated_at: new Date().toISOString() });
    setSaving(false);
    if (!error) {
      onUrgentChange(orderId, checked);
    } else {
      alert(`Failed to update urgent flag: ${error.message}`);
    }
  };

  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
      color: urgent ? 'var(--red-light, #f87171)' : 'var(--muted)',
      fontWeight: urgent ? 700 : 400, cursor: 'pointer',
    }}>
      <input type="checkbox" checked={!!urgent} disabled={saving} onChange={handleChange} />
      Urgent
    </label>
  );
};

// ── Single SO block ───────────────────────────────────────────────────────────
export const SOBlock = ({ order, notes, staffList, nextActions, assignedInitials, urgent, onNoteAdded, onAssign, onUrgentChange, extraHeader }) => {
  const stageInfo   = stageMap[order.stage] || { color: 'muted', label: order.stage || 'Unknown' };
  const customer    = [order.first_name, order.last_name].filter(Boolean).join(' ') || '—';
  const createdDate = order.created_date
    ? new Date(order.created_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const days   = daysSince(order.created_date);
  const isVoid = order.status === 'VOID';

  return (
    <div className="so-group">
      <div className="so-group__header">
        <span className="code-badge">{order.order_ref || order.order_id}</span>
        <span className="so-group__customer">
          {customer}
          {order.email && ` · ${order.email}`}
          {order.phone && ` · ${order.phone}`}
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{createdDate}</span>
        <span className={`pill pill--${stageInfo.color}`}>{stageInfo.label}</span>
        {extraHeader}
        <AssignDropdown
          orderId={order.order_id}
          assignedInitials={assignedInitials}
          staffList={staffList}
          onAssign={onAssign}
        />
        <UrgentCheckbox
          orderId={order.order_id}
          urgent={urgent}
          onUrgentChange={onUrgentChange}
        />
        {isVoid && (
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red, #ef4444)', marginLeft: 6 }}>
            VOID
          </span>
        )}
        {days !== null && (
          <span style={{ fontSize: 11, color: days > 7 ? 'var(--red-light, #f87171)' : 'var(--muted)', marginLeft: 'auto' }}>
            {days}d old
          </span>
        )}
      </div>

      <div className="so-group__items">
        {order.items.length === 0 && (
          <div className="so-item" style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
            No line items — see internal comments below.
          </div>
        )}
        {order.items.map((item, i) => {
          const fullyMoved = Number(item.qty_moved) >= Number(item.qty_ordered);
          return (
            <div key={i} className="so-item">
              <span className="code-badge code-badge--sm">{item.item_code}</span>
              <span className="so-item__name">{item.item_name}</span>
              <span className="so-item__qty">
                ordered <strong>{fmt(item.qty_ordered)}</strong>
                &nbsp;·&nbsp;
                moved <strong className={fullyMoved ? 'qty-ok' : 'qty-short'}>{fmt(item.qty_moved)}</strong>
                {item.po_number && (
                  <span style={{ color: 'var(--muted)', marginLeft: 4 }}>
                    (Ordered on PO: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--code)' }}>{item.po_number}</span>)
                  </span>
                )}
                {' '}
                <span className={`so-item__dot ${fullyMoved ? 'so-item__dot--green' : 'so-item__dot--red'}`}>●</span>
              </span>
            </div>
          );
        })}
      </div>

      {order.internal_comments && (
        <div style={{
          margin: '0 12px 8px', padding: '8px 10px', fontSize: 12,
          background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4,
          color: 'var(--muted)', whiteSpace: 'pre-wrap',
        }}>
          <strong style={{ color: 'var(--white)' }}>Internal comments (Cin7):</strong>
          <div style={{ marginTop: 4 }}>{order.internal_comments}</div>
        </div>
      )}

      <NotesPanel
        orderId={order.order_id}
        stage={order.stage}
        notes={notes}
        staffList={staffList}
        nextActions={nextActions}
        onAdded={onNoteAdded}
      />
    </div>
  );
};
